import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getAdminMe } from '../../services/capabilityService';
import { runAllSmokeChecks } from '../../utils/adminAuthSmokeChecks';

const CapabilityContext = createContext(null);

/**
 * Capability Provider
 * 
 * Loads admin user capabilities once and provides them via context.
 * 
 * Provides:
 * - loading: boolean (true while fetching)
 * - error: Error | null (error if fetch failed)
 * - me: object | null (user info with capabilities)
 * - has(capability): function (check if user has a capability)
 */
export function CapabilityProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const requestVersion = useRef(0);

  const loadCapabilities = useCallback(async (forceRefresh = false) => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    setMe(null);
    try {
      const data = await getAdminMe(forceRefresh);
      if (version === requestVersion.current) {
        setMe(data);
        runAllSmokeChecks({ me: data, has: cap => data.capabilities.includes(cap) });
      }
      return data;
    } catch (err) {
      if (version === requestVersion.current) {
        setError(err);
        const isUnauth = err?.isUnauthenticated || err?.status === 401 || err?.code === 'UNAUTHENTICATED';
        if (isUnauth && !window.location.pathname.startsWith('/admin/login')) {
          window.location.href = '/admin/login';
        }
      }
      throw err;
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, []);

  const refreshCapabilities = useCallback(() => loadCapabilities(true), [loadCapabilities]);

  useEffect(() => {
    // The context exposes the error; initial loading has no caller to catch it.
    loadCapabilities().catch(() => {});
    return () => { requestVersion.current += 1; };
  }, [loadCapabilities]);

  /**
   * Check if user has a specific capability
   * @param {string} capability - Capability to check (e.g., 'admin:integrations:manage')
   * @returns {boolean} True if user has the capability
   */
  const has = (capability) => {
    if (!me || !me.capabilities) {
      return false;
    }
    return me.capabilities.includes(capability);
  };

  // Determine auth state
  const isUnauthenticated = error?.isUnauthenticated || error?.status === 401 || error?.code === 'UNAUTHENTICATED';
  const isUnauthorized = error?.isUnauthorized || error?.status === 403 || error?.code === 'UNAUTHORIZED';
  const isAuthenticated = !loading && !error && me !== null;
  const isSignedOut = isUnauthenticated || (error && !isUnauthorized);

  // Expose isSuperAdmin directly for easy access
  const isSuperAdmin = me?.isSuperAdmin === true;

  const value = {
    loading,
    error,
    me,
    has,
    refreshCapabilities,
    isUnauthenticated,
    isUnauthorized,
    isAuthenticated,
    isSignedOut,
    isSuperAdmin, // Direct access to super admin status
  };

  return (
    <CapabilityContext.Provider value={value}>
      {children}
    </CapabilityContext.Provider>
  );
}

/**
 * Hook to use capability context
 * @returns {object} { loading, error, me, has }
 */
export function useCapabilities() {
  const context = useContext(CapabilityContext);
  if (!context) {
    throw new Error('useCapabilities must be used within CapabilityProvider');
  }
  return context;
}

